import { useCallback } from 'react'

import { Copy, useCopy } from '@mondough/copy'

import { useConsentPreferences } from '../react'
import { Dialog, type DialogProps } from './components/dialog'
import { CookieConsentForm, type CookieConsentFormBaseProps } from './form'

export type CookieConsentDialogProps = CookieConsentFormBaseProps &
  DialogProps & {
    cookiePolicyUrl?: string | URL
  }

export function CookieConsentDialog({
  categories,
  onSubmit,
  onDismiss,
  isDismissable,
  closeButton,
  cookiePolicyUrl = 'https://monzo.com/legal/cookie-notice',
  ...dialogProps
}: CookieConsentDialogProps) {
  const c = useCopy()
  const { acknowledged } = useConsentPreferences()

  const handleSubmit = useCallback(
    (event) => {
      onSubmit?.(event)
      onDismiss?.()
    },
    [onSubmit, onDismiss],
  )

  const title = c({
    key: 'consent-management.cookie-dialog.title',
    message: 'Adjust your cookie preferences',
  })

  return (
    <Dialog
      {...dialogProps}
      onDismiss={onDismiss}
      isDismissable={acknowledged && isDismissable}
      closeButton={acknowledged && closeButton}
      aria-label={title as string}
    >
      <h2>{title}</h2>
      <p>
        <Copy.Simple
          id="consent-management.cookie-dialog.summary"
          fallback="We use 4 types of cookie. You can choose which cookies you’re happy for us to use. For more detail, and a list of the cookies we use, see the <Link>Monzo cookie policy</Link>."
          components={{
            Link: ({ children }) => (
              <a
                href={cookiePolicyUrl.toString()}
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            ),
          }}
        />
      </p>
      <CookieConsentForm categories={categories} onSubmit={handleSubmit} />
    </Dialog>
  )
}
